import React, { useContext } from 'react'
import Button from '@Components/Buttons/Button'
import Pluralize from 'pluralize'
import { StoresContext } from '@App/contexts'
import { useNavigate } from 'react-router'
import { WorksFromUpload } from '@Entities/work/works-from-upload'
import Navigation from '@App/navigation'
import { observer } from 'mobx-react'
import RootStore from '@App/stores/root-store'

interface FileUploadBottomButtonsProps {
    currentStep: number
    backLink?: string
    nextLink?: string
    submittedCountWorks?: number
    setIndividualWorkModalIsOpen: (isOpen: boolean) => void
    setBulkUploadReviewModalIsOpen: (isOpen: boolean) => void
    isDraftsSaved: boolean
    setSidebarIsOpen: (isOpen: boolean) => void
    setRedirectUrl: (url: string) => void
}

const FileUploadBottomButtons = ({
    currentStep,
    backLink,
    setIndividualWorkModalIsOpen,
    setBulkUploadReviewModalIsOpen,
    nextLink,
    submittedCountWorks,
    isDraftsSaved,
    setSidebarIsOpen,
    setRedirectUrl,
}: FileUploadBottomButtonsProps) => {
    const navigate = useNavigate()
    const { bulkUploadStore, workStore, rightsholderStore, userStore, preloaderStore, popupStore } =
        useContext<RootStore>(StoresContext)

    const uploadFile = async () => {
        const rightsHolderId = rightsholderStore.currentRightsholderId

        if (!nextLink) {
            return
        }

        try {
            preloaderStore.openTransparentPreloader(true)
            await bulkUploadStore.getCurrentWorksFromUploadFile(rightsHolderId)
            bulkUploadStore.destroyStartTitlePositionWorkSubmissions()
            preloaderStore.openTransparentPreloader(false)
            navigate(nextLink)
        } catch (ignore) {
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const saveWork = () => {
        const workDraftFromLocal = workStore.workDraftLocal
        if (!workDraftFromLocal) {
            return
        }

        bulkUploadStore.formatWorksForSaving(workDraftFromLocal)

        if (!nextLink) {
            return
        }

        navigate(nextLink)
    }

    const saveAllWorks = async () => {
        const allWorksFromUpload = bulkUploadStore.allWorksFromUpload
        const rightsHolderId = rightsholderStore.currentRightsholderId

        if (!allWorksFromUpload) {
            return
        }

        const worksToSave: WorksFromUpload = {
            ...allWorksFromUpload,
            isComplete: true,
        }

        try {
            preloaderStore.openTransparentPreloader(true)
            await bulkUploadStore.saveUploadWorks(rightsHolderId, worksToSave)

            bulkUploadStore.setCurrentFileId(worksToSave.fileId)

            if (!nextLink) {
                return
            }

            navigate(nextLink)

            // remove start title position
            bulkUploadStore.destroyStartTitlePositionWorkSubmissions()

            // get complete works from backend
            await bulkUploadStore.getCompleteWorksFromUpload(rightsHolderId)
            preloaderStore.openTransparentPreloader(false)
        } catch (e) {
            popupStore.setAlertFromError(e)
            preloaderStore.openTransparentPreloader(false)
        }
    }

    const checkSaveStatusDraft = () => {
        const allWorksFromUpload = bulkUploadStore.allWorksFromUpload
        const rightsHolderId = rightsholderStore.currentRightsholderId

        if (!allWorksFromUpload) {
            return
        }

        // if error works exists
        if (!isDraftsSaved && allWorksFromUpload.error.length) {
            setSidebarIsOpen(true)
        } else {
            bulkUploadStore.destroyAllWorksFromUpload()
            setRedirectUrl(Navigation.rightholder.registrationHistory.root(rightsHolderId))
        }
    }

    return (
        <div className="next-back-bar-bottom-content">
            {currentStep !== 2 && currentStep !== 3 && currentStep !== 4 && (
                <Button mainColor className="back" type="secondary" typeLink={!!backLink} linkTo={backLink}>
                    Back
                </Button>
            )}
            {currentStep === 3 && (
                <Button mainColor className="back" type="secondary" onClick={() => setIndividualWorkModalIsOpen(true)}>
                    Back
                </Button>
            )}
            {currentStep === 2 && (
                <p onClick={() => setBulkUploadReviewModalIsOpen(true)} className="link small bold cancel linkcolor">
                    Cancel and Start Over
                </p>
            )}
            <div />
            <div className="right">
                {currentStep === 1 && (
                    <Button
                        mainColor
                        type="primary"
                        className="next"
                        onClick={uploadFile}
                        disabled={!workStore.wizardStepIsValid}
                    >
                        Upload
                    </Button>
                )}

                {currentStep === 2 && (
                    <Button
                        mainColor
                        type="primary"
                        className="next"
                        onClick={saveAllWorks}
                        disabled={!submittedCountWorks || userStore.isInternal}
                    >{`Submit ${Pluralize('work', submittedCountWorks, true)}`}</Button>
                )}

                {currentStep === 3 && (
                    <Button
                        mainColor
                        type="primary"
                        className="next"
                        onClick={() => {
                            saveWork()
                        }}
                        disabled={!workStore.wizardStepIsValid}
                    >
                        Save
                    </Button>
                )}

                {currentStep === 4 && (
                    <Button mainColor type="primary" className="next" onClick={checkSaveStatusDraft}>
                        Done
                    </Button>
                )}
            </div>
        </div>
    )
}

export default observer(FileUploadBottomButtons)
